import {
	Button,
	ButtonGroup,
	ButtonGroupItem,
	ChipConfirmModal,
	Input,
	Label,
	Badge as UiBadge,
	Skeleton as UiSkeleton,
} from "@ondit/ui";
import { Search } from "lucide-react";
import {
	cloneElement,
	createContext,
	isValidElement,
	type ReactNode,
	useCallback,
	useContext,
	useId,
	useMemo,
	useState,
} from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useI18n, useT } from "@/i18n";
import { ApiError } from "@/lib/api";
import type { AsyncState } from "@/lib/hooks";

// -- page furniture -----------------------------------------------------------

export function PageHead({
	title,
	lede,
	crumbs,
	children,
}: {
	title: ReactNode;
	lede?: ReactNode;
	crumbs?: { to: string; label: string }[];
	children?: ReactNode;
}) {
	return (
		<div>
			{crumbs ? (
				<div className="crumbs">
					{crumbs.map((c) => (
						<span key={c.to}>
							<Link to={c.to}>{c.label}</Link>
							<span> / </span>
						</span>
					))}
				</div>
			) : null}
			<div className="page-head">
				<div>
					<h1>{title}</h1>
					{lede ? <p className="lede">{lede}</p> : null}
				</div>
				{children ? <div className="tools">{children}</div> : null}
			</div>
		</div>
	);
}

export function Stat({
	label,
	value,
	foot,
	tone,
}: {
	label: ReactNode;
	value: ReactNode;
	foot?: ReactNode;
	tone?: "warn" | "danger";
}) {
	return (
		<div className={`stat${tone ? ` is-${tone}` : ""}`}>
			<span className="label">{label}</span>
			<span className="value">{value}</span>
			{foot ? <span className="foot">{foot}</span> : null}
		</div>
	);
}

export function Badge({
	tone = "neutral",
	children,
}: {
	tone?: "neutral" | "success" | "warning" | "danger" | "accent" | "outline";
	children: ReactNode;
}) {
	const colours = {
		neutral: "bg-surface-alt text-fg-2",
		success: "bg-(--success-soft) text-(--success)",
		warning: "bg-(--warning-soft) text-(--warning)",
		danger: "bg-(--danger-soft) text-(--danger)",
		accent: "bg-ink text-ink-fg",
		outline: "border-border bg-transparent text-fg",
	};
	return (
		<UiBadge variant="gray" className={`badge ${colours[tone]}`}>
			{children}
		</UiBadge>
	);
}

/** A status word with its colour. One table for every status the console shows. */
export function Status({ value, prefix }: { value: string; prefix: string }) {
	const t = useT();
	const tone = TONES[value] ?? "neutral";
	const key = `${prefix}.${value}`;
	const label = t(key);
	return <Badge tone={tone}>{label === key ? value : label}</Badge>;
}

const TONES: Record<string, "neutral" | "success" | "warning" | "danger" | "accent" | "outline"> = {
	published: "success",
	listed: "success",
	done: "success",
	active: "success",
	approved: "success",
	verified: "success",
	review: "warning",
	pending: "warning",
	queued: "outline",
	running: "accent",
	past_due: "warning",
	paused: "warning",
	draft: "outline",
	hidden: "danger",
	failed: "danger",
	rejected: "danger",
	canceled: "danger",
	unpublished: "danger",
	skipped: "neutral",
	cancelled: "neutral",
	duplicate: "neutral",
	merged: "neutral",
	none: "outline",
	expired: "danger",
};

export function KV({ items }: { items: [ReactNode, ReactNode][] }) {
	return (
		<dl className="kv">
			{items.map(([k, v], i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: static rows
				<div key={i} style={{ display: "contents" }}>
					<dt>{k}</dt>
					<dd>{v ?? "—"}</dd>
				</div>
			))}
		</dl>
	);
}

export function Json({ value }: { value: unknown }) {
	return <pre>{JSON.stringify(value, null, 2)}</pre>;
}

export function Field({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
	const id = useId();
	const control = isValidElement<{ id?: string; "aria-describedby"?: string }>(children) ? children : null;
	const controlId = control?.props.id ?? id;
	return (
		<div className="field">
			<Label className="field-label" htmlFor={control ? controlId : undefined}>
				{label}
			</Label>
			{control
				? cloneElement(control, {
						id: controlId,
						"aria-describedby": hint ? `${id}-hint` : control.props["aria-describedby"],
					})
				: children}
			{hint ? (
				<span id={`${id}-hint`} className="hint">
					{hint}
				</span>
			) : null}
		</div>
	);
}

export function SearchBox({
	value,
	onChange,
	placeholder,
}: {
	value: string;
	onChange: (next: string) => void;
	placeholder?: string;
}) {
	const t = useT();
	return (
		<Label className="search">
			<Search aria-hidden />
			<Input
				data-focus="wrapper"
				type="search"
				aria-label={placeholder ?? t("state.search")}
				className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
				value={value}
				placeholder={placeholder ?? t("state.search")}
				onChange={(e) => onChange(e.target.value)}
			/>
		</Label>
	);
}

export function Segmented<T extends string>({
	value,
	onChange,
	options,
}: {
	value: T;
	onChange: (next: T) => void;
	options: { value: T; label: ReactNode }[];
}) {
	return (
		<ButtonGroup
			value={value}
			onValueChange={(next) => {
				if (next) onChange(next as T);
			}}
			className="justify-start flex-wrap"
		>
			{options.map((o) => (
				<ButtonGroupItem key={o.value} value={o.value}>
					{o.label}
				</ButtonGroupItem>
			))}
		</ButtonGroup>
	);
}

/**
 * The foot of a paged table. "Load more" shows while the last page came back
 * full — the server's word, not the client's arithmetic — and never past a
 * total the list knows.
 */
export function Pager({
	shown,
	total,
	hasMore,
	busy,
	onMore,
}: {
	shown: number;
	total?: number;
	hasMore: boolean;
	busy?: boolean;
	onMore: () => void;
}) {
	const t = useT();
	const more = hasMore && (total === undefined || shown < total);
	return (
		<div className="table-foot">
			<span>{total === undefined ? t("state.shown", { shown }) : t("state.of", { shown, total })}</span>
			{more ? (
				<Button variant="default" type="button" disabled={busy} onClick={onMore}>
					{t("state.more")}
				</Button>
			) : null}
		</div>
	);
}

// -- states -------------------------------------------------------------------

/** A session that has ended sends the operator back through the door, and back here after it. */
function toLogin() {
	window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
}

function Skeleton() {
	const t = useT();
	return (
		<div className="loading-placeholder" role="status" aria-label={t("state.loading")}>
			<UiSkeleton className="h-[calc(var(--control-h)*3)]" />
			<UiSkeleton className="h-(--s-9) w-3/5" />
			<UiSkeleton className="h-(--s-9) w-2/5" />
		</div>
	);
}

export function Empty({ title, body }: { title?: string; body?: string }) {
	const t = useT();
	return (
		<div className="empty">
			<h2>{title ?? t("state.empty")}</h2>
			{body ? <p>{body}</p> : null}
		</div>
	);
}

export function Async<T>({
	state,
	children,
	empty,
	pending,
}: {
	state: AsyncState<T>;
	children: (data: T) => ReactNode;
	empty?: ReactNode;
	pending?: ReactNode;
}) {
	const t = useT();
	const { locale } = useI18n();
	if (state.loading && state.data === null) return pending ?? <Skeleton />;
	if (state.error) {
		if (state.error.isUnauthorized) {
			toLogin();
			return <Skeleton />;
		}
		return (
			<div className="empty" role="alert">
				<h2>{t("state.error")}</h2>
				<p>{state.error.localized(locale)}</p>
				<Button variant="ghost" type="button" onClick={state.reload}>
					{t("state.retry")}
				</Button>
			</div>
		);
	}
	if (state.data === null) return empty ?? <Skeleton />;
	return <>{children(state.data)}</>;
}

/**
 * Run an action, report how it went.
 *
 * Every button in the console goes through this: the failure is the server's
 * sentence in the operator's language, and the success is whatever the
 * caller wants to say — usually the new number.
 */
export function useAction() {
	const { locale } = useI18n();
	const [busy, setBusy] = useState(false);
	const run = useCallback(
		async <T,>(action: () => Promise<T>, done?: (result: T) => string | void): Promise<T | undefined> => {
			setBusy(true);
			try {
				const result = await action();
				const said = done?.(result);
				if (said) toast(said);
				return result;
			} catch (error) {
				// A 401 on a click is a session that ended under the operator —
				// the page loader already goes back through the door for that,
				// and a button should not answer it with "sign in" as a toast.
				if (error instanceof ApiError && error.isUnauthorized) {
					toLogin();
					return undefined;
				}
				toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
				return undefined;
			} finally {
				setBusy(false);
			}
		},
		[locale],
	);
	return { run, busy };
}

// -- confirm ------------------------------------------------------------------

interface Ask {
	title: string;
	body?: string;
	danger?: boolean;
	resolve: (ok: boolean) => void;
}

const ConfirmContext = createContext<(title: string, body?: string, danger?: boolean) => Promise<boolean>>(
	async () => true,
);

export function ConfirmProvider({ children }: { children: ReactNode }) {
	const t = useT();
	const [ask, setAsk] = useState<Ask | null>(null);

	const confirm = useCallback(
		(title: string, body?: string, danger?: boolean) =>
			new Promise<boolean>((resolve) => setAsk({ title, body, danger, resolve })),
		[],
	);

	const answer = (ok: boolean) => {
		ask?.resolve(ok);
		setAsk(null);
	};

	return (
		<ConfirmContext.Provider value={confirm}>
			{children}
			{ask && (
				<ChipConfirmModal
					open
					onOpenChange={(open) => {
						if (!open) answer(false);
					}}
					title={ask.title}
					text={ask.body ?? ask.title}
					dismissLabel={t("state.cancel")}
					confirm={{ label: t("state.confirm"), variant: "primary", onClick: () => answer(true) }}
				/>
			)}
		</ConfirmContext.Provider>
	);
}

export function useConfirm() {
	return useContext(ConfirmContext);
}

/** A list of lines to an array, and back. For the "one per line" fields. */
export function useLines(initial: string[]): [string, (next: string) => void, string[]] {
	const [text, setText] = useState(initial.join("\n"));
	const lines = useMemo(
		() =>
			text
				.split("\n")
				.map((l) => l.trim())
				.filter(Boolean),
		[text],
	);
	return [text, setText, lines];
}
